import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import PageHero from "../components/PageHero";

const WEB3FORMS_ACCESS_KEY = "73ee46f9-bbb7-4ca0-8c37-f0456ddb28ac";

const EMPTY = { name: "", mobile: "", email: "", reason: "" };

export default function DeleteAccount() {
  const [form, setForm] = useState(EMPTY);
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const API_BASE = process.env.REACT_APP_API_URL || "https://api.instafrnd.com";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) return setError("Please enter your name.");
    if (!/^\d{10}$/.test(form.mobile.trim()))
      return setError("Please enter the 10-digit mobile number linked to your account.");
    if (!confirmed)
      return setError("Please confirm you understand this permanently deletes your account.");

    setSending(true);
    try {
      // Primary: internal API — records the deletion request in the admin panel.
      const res = await fetch(`${API_BASE}/api/public/account-deletion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          mobile: form.mobile,
          email: form.email || undefined,
          reason: form.reason || undefined,
          kind: "account_deletion",
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitted(true);
        setForm(EMPTY);
        setConfirmed(false);
        return;
      }
      throw new Error(data.message || "Server error");
    } catch (_primaryErr) {
      // Fallback: web3forms so a deletion request is never silently lost.
      try {
        const fallback = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            access_key: WEB3FORMS_ACCESS_KEY,
            subject: "Account Deletion Request — Instafrnd",
            from_name: "Instafrnd Account Deletion",
            name: form.name,
            mobile: form.mobile,
            email: form.email,
            reason: form.reason,
          }),
        });
        const fbData = await fallback.json();
        if (fbData.success) {
          setSubmitted(true);
          setForm(EMPTY);
          setConfirmed(false);
          return;
        }
      } catch (_) {
        /* ignore fallback error */
      }
      setError("Something went wrong. Please try again, or email connect@instafrnd.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Delete Your Account — Instafrnd</title>
        <meta name="description" content="Request permanent deletion of your Instafrnd account and associated data." />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href="https://instafrnd.com/delete-account" />
      </Helmet>
      <PageHero
        eyebrow="Delete Account"
        title="Delete your Instafrnd account."
        subtitle="You can permanently delete your account and personal data at any time. Here's how it works and what gets removed."
      />

      <section className="features">
        <div className="wrap content-narrow prose reveal">
          <h3>How to delete your account</h3>
          <p>
            The fastest way is from inside the app: go to{" "}
            <strong>Profile → Settings → Account → Delete Account</strong> and follow the
            prompts. If you no longer have the app installed, submit the form below with the
            mobile number linked to your account and our team will process the request.
          </p>

          <h3>What gets deleted</h3>
          <p>
            Your profile and photos, bio and interests, chat history, connections, call and
            room history, reputation score, and account login. This action is permanent and
            cannot be undone.
          </p>

          <h3>What is retained (and for how long)</h3>
          <p>
            To meet legal, tax, fraud-prevention and dispute-resolution obligations, we retain
            a limited set of transaction and payment records (as required by Indian law) for up
            to the statutory retention period, after which they are deleted. Any unused,
            purchased coins are non-refundable on deletion.
          </p>

          <h3>Timeline</h3>
          <p>
            Requests submitted here are actioned within <strong>30 days</strong>. You'll receive
            a confirmation once your account and eligible data have been removed.
          </p>
        </div>
      </section>

      <section className="section-pad" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <form className="form-card reveal" onSubmit={onSubmit} noValidate>
            <div className="form-row">
              <div className="field">
                <label htmlFor="name">Name *</label>
                <input id="name" name="name" value={form.name} onChange={update} placeholder="Your full name" />
              </div>
              <div className="field">
                <label htmlFor="mobile">Registered Mobile Number *</label>
                <input id="mobile" name="mobile" value={form.mobile} onChange={update} maxLength={10} inputMode="numeric" placeholder="10-digit number on your account" />
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="email">Email (optional)</label>
                <input id="email" name="email" value={form.email} onChange={update} placeholder="For your deletion confirmation" />
              </div>
              <div className="field">
                <label htmlFor="reason">Reason (optional)</label>
                <input id="reason" name="reason" value={form.reason} onChange={update} placeholder="Help us improve" />
              </div>
            </div>

            <label className="checkbox-row" style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 22 }}>
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 4 }} />
              <span>I understand this permanently deletes my Instafrnd account and data, and that unused purchased coins are non-refundable.</span>
            </label>

            <button className="btn btn-primary" type="submit" disabled={sending} style={{ width: "100%", justifyContent: "center", padding: "14px", opacity: sending ? 0.7 : 1, cursor: sending ? "not-allowed" : "pointer" }}>
              {sending ? "Submitting…" : "Request Account Deletion"}
            </button>

            {error && <div className="form-note" style={{ color: "#E5484D" }}>{error}</div>}
            {submitted && (
              <div className="form-note ok">
                ✅ Your deletion request has been received. We'll process it within 30 days and confirm by email if provided.
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}
